import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, AlertCircle, RefreshCw, Save, Link2, Layers, AlertTriangle, CheckCircle2, ArrowRight, Search, X } from 'lucide-react';

// Pulse source options (static dropdown)
const PULSE_SOURCES = [
  { id: 'SRC01', name: 'ODM Fresh', cohort: 'ODM' },
  { id: 'SRC02', name: 'Reference', cohort: 'referral' },
  { id: 'SRC03', name: 'Direct Walk-in', cohort: 'Walk-in' },
  { id: 'SRC04', name: 'Unpaid/Expired', cohort: 'unpaid' },
  { id: 'SRC05', name: 'Renewal - Same Month', cohort: 'renewal' },
  { id: 'SRC06', name: 'Renewal - Advanced', cohort: 'renewal' },
];

// Badge color mapping by source
const SOURCE_BADGE_CLASS = {
  'SRC01': 'info',
  'SRC02': 'success',
  'SRC03': 'bg-purple-100 text-purple-700',
  'SRC04': 'warning',
  'SRC05': 'bg-teal-100 text-teal-700',
  'SRC06': 'bg-cyan-100 text-cyan-700',
};

function SourceBadge({ sourceId }) {
  const cls = SOURCE_BADGE_CLASS[sourceId];
  // Use status-badge class for standard colors, inline for custom
  if (['info', 'success', 'warning', 'error'].includes(cls)) {
    return <span className={`status-badge ${cls}`}>{sourceId}</span>;
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {sourceId}
    </span>
  );
}

function getSourceName(sourceId) {
  return PULSE_SOURCES.find(s => s.id === sourceId)?.name || sourceId;
}

export default function LeadSourceMappingTab() {
  const [mappings, setMappings] = useState([]);
  const [unmappedSources, setUnmappedSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({ wtf_source: '', pulse_source_id: '', cohort: '' });
  const [isCacheEmpty, setIsCacheEmpty] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMappings();
    fetchUnmappedSources();
  }, []);

  const fetchMappings = async () => {
    try {
      const response = await axiosInstance.get('/lead-source-mappings');
      if (response.data.status === 'success') {
        setMappings(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching mappings:', error);
      toast.error('Failed to load lead source mappings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnmappedSources = async () => {
    try {
      const response = await axiosInstance.get('/lead-source-mappings/unmapped');
      if (response.data.status === 'success') {
        setUnmappedSources(response.data.data || []);
        setIsCacheEmpty(response.data.cache_empty || false);
      }
    } catch (error) {
      console.error('Error fetching unmapped sources:', error);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await axiosInstance.post('/lead-source-mappings/seed');
      if (response.data.status === 'success') {
        toast.success(`Seeded ${response.data.upserted} default mappings`);
        fetchMappings();
        fetchUnmappedSources();
      }
    } catch (error) {
      console.error('Error seeding mappings:', error);
      toast.error('Failed to seed default mappings');
    }
  };

  const handleSave = async () => {
    if (!formData.wtf_source || !formData.pulse_source_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axiosInstance.post('/lead-source-mappings', formData);
      if (response.data.status === 'success') {
        toast.success(editingMapping ? 'Mapping updated' : 'Mapping created');
        setShowAddDialog(false);
        setEditingMapping(null);
        setFormData({ wtf_source: '', pulse_source_id: '', cohort: '' });
        fetchMappings();
        fetchUnmappedSources();
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Failed to save mapping');
    }
  };

  const handleDelete = async (wtf_source) => {
    if (!window.confirm(`Delete mapping for "${wtf_source}"?`)) return;

    try {
      const response = await axiosInstance.delete(`/lead-source-mappings/${encodeURIComponent(wtf_source)}`);
      if (response.data.status === 'success') {
        toast.success('Mapping deleted');
        fetchMappings();
        fetchUnmappedSources();
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error('Failed to delete mapping');
    }
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      wtf_source: mapping.wtf_source,
      pulse_source_id: mapping.pulse_source_id,
      cohort: mapping.cohort
    });
    setShowAddDialog(true);
  };

  const handleAddFromUnmapped = (wtf_source) => {
    setEditingMapping(null);
    setFormData({ wtf_source, pulse_source_id: '', cohort: '' });
    setShowAddDialog(true);
  };

  const handlePulseSourceChange = (sourceId) => {
    const source = PULSE_SOURCES.find(s => s.id === sourceId);
    setFormData({ ...formData, pulse_source_id: sourceId, cohort: source ? source.cohort : '' });
  };

  const resetForm = () => {
    setEditingMapping(null);
    setFormData({ wtf_source: '', pulse_source_id: '', cohort: '' });
  };

  // Summary stats
  const stats = useMemo(() => {
    const byCohort = {};
    for (const m of mappings) {
      byCohort[m.pulse_source_id] = (byCohort[m.pulse_source_id] || 0) + 1;
    }
    return {
      total: mappings.length,
      cohorts: Object.keys(byCohort).length,
      unmapped: unmappedSources.length,
      byCohort,
    };
  }, [mappings, unmappedSources]);

  // Grouped + filtered
  const groupedMappings = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = q
      ? mappings.filter(m => m.wtf_source.toLowerCase().includes(q) || m.cohort.toLowerCase().includes(q) || m.pulse_source_id.toLowerCase().includes(q))
      : mappings;

    return PULSE_SOURCES.map(src => ({
      ...src,
      mappings: filtered.filter(m => m.pulse_source_id === src.id),
    })).filter(g => g.mappings.length > 0);
  }, [mappings, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Link2 size={20} /> Lead Source Mapping ({mappings.length})
        </h3>
        <div className="flex gap-2">
          {mappings.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults}>
              <Layers size={16} className="mr-2" /> Seed Defaults
            </Button>
          )}
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button><Plus size={16} className="mr-2" /> Add Mapping</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingMapping ? 'Edit Mapping' : 'Add Lead Source Mapping'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>WTF Source Name *</Label>
                  <Input
                    value={formData.wtf_source}
                    onChange={(e) => setFormData({ ...formData, wtf_source: e.target.value })}
                    placeholder="e.g., cold_calling, walkin, referral"
                    disabled={!!editingMapping || (formData.wtf_source !== '' && unmappedSources.includes(formData.wtf_source))}
                  />
                  <p className="text-xs text-gray-500">Exact source name from WTF API (case-sensitive)</p>
                </div>
                <div className="space-y-2">
                  <Label>Pulse Source *</Label>
                  <Select value={formData.pulse_source_id} onValueChange={handlePulseSourceChange}>
                    <SelectTrigger><SelectValue placeholder="Select pulse source..." /></SelectTrigger>
                    <SelectContent>
                      {PULSE_SOURCES.map(source => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.id} - {source.name} ({source.cohort})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cohort (auto-filled)</Label>
                  <Input value={formData.cohort} disabled className="bg-gray-100 text-gray-500" />
                </div>
                {formData.pulse_source_id && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
                    <CheckCircle2 size={16} className="text-blue-500 flex-shrink-0" />
                    <span>
                      <strong>{formData.wtf_source || '(source)'}</strong>
                      <ArrowRight size={14} className="inline mx-1" />
                      <SourceBadge sourceId={formData.pulse_source_id} /> {getSourceName(formData.pulse_source_id)}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>Cancel</Button>
                <Button onClick={handleSave}><Save size={16} className="mr-2" /> Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Link2 size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Mappings</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
              <Layers size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.cohorts}</p>
              <p className="text-xs text-gray-500">Pulse Cohorts</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${stats.unmapped > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
              {stats.unmapped > 0 ? (
                <AlertTriangle size={20} className="text-amber-600" />
              ) : (
                <CheckCircle2 size={20} className="text-green-600" />
              )}
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.unmapped}</p>
              <p className="text-xs text-gray-500">{stats.unmapped > 0 ? 'Unmapped Sources' : 'All Sources Mapped'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Unmapped Sources Warning */}
      {unmappedSources.length > 0 && !isCacheEmpty && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800">Unmapped WTF Sources Detected</h4>
              <p className="text-xs text-amber-700 mt-1">
                These sources from the WTF API don't have mappings yet and will default to SRC01 (ODM Fresh).
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {unmappedSources.map(source => (
                  <button
                    key={source}
                    onClick={() => handleAddFromUnmapped(source)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white border border-amber-300 text-amber-800 rounded-full hover:bg-amber-100 cursor-pointer transition-colors duration-150"
                  >
                    {source}
                    <Plus size={12} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search (only when there are mappings) */}
      {mappings.length > 0 && (
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by source name, cohort, or ID..."
            className="pl-9 max-w-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {mappings.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Link2 size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No lead source mappings configured</p>
          <p className="text-sm text-gray-400 mt-1">Click "Seed Defaults" to populate 15 standard WTF source mappings, or add them manually.</p>
        </div>
      ) : groupedMappings.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Search size={32} className="mx-auto text-gray-300 mb-2" />
          <p>No mappings match "{searchQuery}"</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedMappings.map(group => (
            <div key={group.id} className="data-table">
              {/* Group header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                <div className="flex items-center gap-2">
                  <SourceBadge sourceId={group.id} />
                  <span className="text-sm font-semibold text-gray-700">{group.name}</span>
                  <span className="text-xs text-gray-400">({group.cohort})</span>
                </div>
                <span className="text-xs text-gray-500">{group.mappings.length} source{group.mappings.length !== 1 ? 's' : ''}</span>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>WTF Source</th>
                    <th>Cohort</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.mappings.map(mapping => (
                    <tr key={mapping.wtf_source}>
                      <td>
                        <code className="text-sm bg-gray-100 px-2 py-0.5 rounded text-gray-700 font-mono">
                          {mapping.wtf_source}
                        </code>
                      </td>
                      <td>
                        <span className="status-badge success">{mapping.cohort}</span>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(mapping)} title="Edit" className="cursor-pointer">
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(mapping.wtf_source)} title="Delete" className="text-red-600 hover:text-red-700 cursor-pointer">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      {mappings.length > 0 && (
        <div className="mt-6 p-3 bg-gray-50 rounded-lg border">
          <p className="text-xs text-gray-500 font-medium mb-2">Pulse Source Reference</p>
          <div className="flex flex-wrap gap-3">
            {PULSE_SOURCES.map(src => (
              <div key={src.id} className="flex items-center gap-1.5 text-xs text-gray-600">
                <SourceBadge sourceId={src.id} />
                <span>{src.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
